import { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { ClipboardCheck, PlayCircle, User as UserIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useTelegramAutoLogin } from '@/hooks/useTelegramAutoLogin';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useT } from '@/contexts/I18nContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import {
  getVisitsZoneSettings,
  listZoneVisits,
  startVisit,
  type VisitStartAmbiguousDto,
  type VisitSummaryDto,
} from '@/api/visits';
import { TotalScoreBadge } from '@/components/visits/scoreDisplay';

/** True when err looks like the 409 "zone has more than one template"
 *  response startVisit() throws (see api/visits.ts's VisitStartAmbiguousDto). */
function asAmbiguousStart(err: unknown): VisitStartAmbiguousDto | null {
  if (typeof err !== 'object' || err === null || !('response' in err)) return null;
  const resp = (err as { response?: { status?: number; data?: unknown } }).response;
  if (resp?.status !== 409) return null;
  const data = resp.data as Partial<VisitStartAmbiguousDto> | undefined;
  return Array.isArray(data?.templates) ? (data as VisitStartAmbiguousDto) : null;
}

export default function VisitsPage() {
  useTelegramAutoLogin();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const navigate = useNavigate();
  const { zoneId } = useCurrentZone();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [templateChoices, setTemplateChoices] = useState<{ id: number; name: string }[] | null>(null);
  const [chosenTemplateId, setChosenTemplateId] = useState('');

  usePageTitle(t('visits.liveTitle'), t('visits.liveSubtitle'));

  const { data: settings } = useSWR(
    zoneId ? ['visits:settings', zoneId] : null,
    async () => (await getVisitsZoneSettings(zoneId!)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const { data, error, isLoading, mutate } = useSWR<{ results: VisitSummaryDto[] }>(
    zoneId && settings?.enabled ? ['visits:list', zoneId] : null,
    async () => (await listZoneVisits(zoneId!)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  if (settings && !settings.enabled) {
    return (
      <div className="container mx-auto max-w-4xl space-y-4 px-4 py-6">
        <h1 className="sr-only">{t('visits.liveTitle')}</h1>
        <Card>
          <CardContent className="space-y-2 py-6 text-center">
            <ClipboardCheck className="mx-auto size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('visits.notEnabled')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const visits = data?.results ?? [];
  const draft = visits.find(v => v.is_draft) ?? null;
  const submitted = visits.filter(v => !v.is_draft);
  const apiMissing = !!error && !data;

  const handleStart = async (templateId?: number) => {
    if (!zoneId) return;
    setStarting(true);
    setStartError(null);
    try {
      const resp = await startVisit(zoneId, templateId);
      await mutate();
      navigate(`/usage/visits/${resp.data.id}`);
    } catch (e) {
      const ambiguous = asAmbiguousStart(e);
      if (ambiguous) {
        setTemplateChoices(ambiguous.templates);
      } else {
        setStartError(extractApiError(e, t('error.general')));
      }
    } finally {
      setStarting(false);
    }
  };

  const handleConfirmTemplate = () => {
    if (!chosenTemplateId) return;
    setTemplateChoices(null);
    handleStart(Number(chosenTemplateId));
  };

  return (
    <div className="container mx-auto max-w-4xl space-y-4 px-4 py-6">
      <h1 className="sr-only">{t('visits.liveTitle')}</h1>

      <Card>
        <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {draft ? t('visits.draftInProgress') : t('visits.startHint')}
          </div>
          <Button
            onClick={() => (draft ? navigate(`/usage/visits/${draft.id}`) : handleStart())}
            disabled={starting}
            className="gap-1.5 sm:w-auto"
          >
            <PlayCircle className="size-4" />
            {draft ? t('visits.continueDraft') : t('visits.startVisit')}
          </Button>
        </CardContent>
        {startError && <CardContent className="pt-0 text-xs text-destructive">{startError}</CardContent>}
        {templateChoices && (
          <CardContent className="flex flex-wrap items-center gap-2 pt-0">
            <span className="text-xs text-muted-foreground">{t('visits.chooseTemplatePrompt')}</span>
            <div className="max-w-[16rem]">
              <Select
                value={chosenTemplateId}
                onChange={e => setChosenTemplateId(e.target.value)}
                aria-label={t('visits.chooseTemplatePrompt')}
                className="h-8 text-xs"
              >
                <option value="" disabled>{t('visits.chooseTemplatePrompt')}</option>
                {templateChoices.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </Select>
            </div>
            <Button size="sm" disabled={!chosenTemplateId || starting} onClick={handleConfirmTemplate}>
              {t('visits.startVisit')}
            </Button>
          </CardContent>
        )}
      </Card>

      {isLoading && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">{t('common.loading')}</CardContent>
        </Card>
      )}

      {apiMissing && (
        <Card>
          <CardContent className="p-0"><ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} /></CardContent>
        </Card>
      )}

      {!isLoading && !apiMissing && visits.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-6 text-center">
            <ClipboardCheck className="mx-auto size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('visits.empty')}</p>
          </CardContent>
        </Card>
      )}

      {draft && (
        <VisitCard visit={draft} locale={locale} onClick={() => navigate(`/usage/visits/${draft.id}`)} />
      )}
      {submitted.length > 0 && (
        <div className="space-y-2.5">
          {submitted.map(v => (
            <VisitCard key={v.id} visit={v} locale={locale} onClick={() => navigate(`/usage/visits/${v.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function VisitCard({
  visit,
  locale,
  onClick,
}: {
  visit: VisitSummaryDto;
  locale: string;
  onClick: () => void;
}) {
  const { t } = useT();
  const time = new Date(visit.visit_time).toLocaleString(locale, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
  return (
    <Card
      className={cn(
        'relative cursor-pointer overflow-hidden pl-1 transition-shadow hover:shadow-md',
        visit.is_draft && 'ring-1 ring-primary/40',
      )}
      onClick={onClick}
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', visit.is_draft ? 'bg-primary' : 'bg-border')} aria-hidden />
      <CardContent className="flex flex-col gap-2 py-3.5 pl-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {visit.is_draft && <Badge variant="default">{t('visits.draftBadge')}</Badge>}
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <UserIcon className="size-3.5" />
              {visit.author.name}
            </span>
            <span className="text-xs text-muted-foreground">{time}</span>
          </div>
          {visit.comment && (
            <p className="line-clamp-1 text-xs text-muted-foreground">{visit.comment}</p>
          )}
        </div>
        <TotalScoreBadge total={visit.total} />
      </CardContent>
    </Card>
  );
}
